import type { Agent, Ticket, TicketPriority, TicketStatus } from '../types';
import type { Flight } from '../types';
import type { QuoteRecord } from '../types';
import { API_BASE } from './env';
import { resolveBackupSmtpForCurrentRoute, type AgentEmailBackupSmtpConfig } from './skyluxStore';

export type AirportSuggestion = { id: string; name: string; city: string; label: string };
export type AirportResolution = {
  resolvedId: string;
  resolvedName: string;
  city: string;
  label: string;
};

export { API_BASE, resolveApiBase } from './env';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? 'Request failed');
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export function fetchTickets() {
  return request<Ticket[]>('/api/tickets');
}

export function searchTickets(query: string) {
  const qs = new URLSearchParams({ q: query.trim() });
  return request<Ticket[]>(`/api/tickets?${qs.toString()}`);
}

export function createTicket(payload: {
  title: string;
  description: string;
  clientName: string;
  clientEmail: string;
  priority: TicketPriority;
  assignee?: string;
  tags: string[];
}) {
  return request<Ticket>('/api/tickets', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateTicketStatus(ticketId: string, status: TicketStatus) {
  return request<Ticket>(`/api/tickets/${ticketId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function addTicketNote(ticketId: string, payload: { message: string; author: string }) {
  return request<Ticket>(`/api/tickets/${ticketId}/notes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function searchFlights(payload: {
  departureId: string;
  arrivalId: string;
  outboundDate: string;
  returnDate?: string;
  type?: '1' | '2';
  adults?: number;
  bags?: number;
  stops?: '0' | '1' | '2' | '3';
  travelClass?: '1' | '2' | '3' | '4';
  currency?: string;
  hl?: string;
  gl?: string;
  deepSearch?: boolean;
}) {
  return request<{
    flights: Flight[];
    priceInsights?: unknown;
    searchMetadata?: unknown;
    tripType?: '1' | '2';
    departureTokensCount?: number;
  }>(
    '/api/flights/search',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function searchReturnFlights(payload: {
  departureToken: string;
  departureId: string;
  arrivalId: string;
  returnDate: string;
  outboundDate: string;
  type?: '1' | '2';
  adults?: number;
  bags?: number;
  stops?: '0' | '1' | '2' | '3';
  travelClass?: '1' | '2' | '3' | '4';
  currency?: string;
  hl?: string;
  gl?: string;
  deepSearch?: boolean;
}) {
  return request<{ flights: Flight[]; searchMetadata?: unknown }>('/api/flights/search-return', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function fetchQuotes() {
  return request<QuoteRecord[]>('/api/quotes');
}

export function fetchQuoteById(id: string) {
  return request<QuoteRecord>(`/api/quotes/${encodeURIComponent(id)}`);
}

export function fetchQuoteByTicketNumber(ticketNumber: string) {
  return request<QuoteRecord>(`/api/quotes/by-ticket/${encodeURIComponent(ticketNumber)}`);
}

export function createQuote(payload: QuoteRecord) {
  return request<QuoteRecord>('/api/quotes', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** Client completes passenger names on the booking page before payment. */
export function patchQuoteBooking(
  quoteId: string,
  payload: { passengers: NonNullable<QuoteRecord['passengers']> },
) {
  return request<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}/booking`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

/** Persist payment / e-ticket delivery flags without replacing the full quote. */
export function patchQuoteDelivery(
  quoteId: string,
  payload: {
    paymentStatus?: QuoteRecord['paymentStatus'];
    emailsSent?: QuoteRecord['emailsSent'];
    eTicketNumber?: string;
  },
) {
  return request<QuoteRecord>(`/api/quotes/${encodeURIComponent(quoteId)}/delivery`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  });
}

export type PayPalConfig = {
  enabled: boolean;
  clientId: string | null;
  mode: 'sandbox' | 'live';
};

export function fetchPayPalConfig() {
  return request<PayPalConfig>('/api/paypal/config');
}

export function createPayPalOrder(quoteId: string, tipUsd = 0) {
  return request<{ id: string; status: string }>('/api/paypal/orders', {
    method: 'POST',
    body: JSON.stringify({ quoteId, tipUsd }),
  });
}

export function capturePayPalOrder(orderId: string, quoteId: string, tipUsd = 0) {
  return request<{ ok: boolean; quote: QuoteRecord }>(
    `/api/paypal/orders/${encodeURIComponent(orderId)}/capture`,
    {
      method: 'POST',
      body: JSON.stringify({ quoteId, tipUsd }),
    },
  );
}

export function fetchFlights(query = '') {
  if (!query.trim()) return request<Flight[]>('/api/flights');
  const qs = new URLSearchParams({ q: query.trim() });
  return request<Flight[]>(`/api/flights?${qs.toString()}`);
}

export function createFlight(payload: Omit<Flight, 'id'>) {
  return request<Flight>('/api/flights', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateFlight(id: string, payload: Partial<Omit<Flight, 'id'>>) {
  return request<Flight>(`/api/flights/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteFlight(id: string) {
  return request<void>(`/api/flights/${id}`, {
    method: 'DELETE',
  });
}

export function autocompleteAirports(query: string) {
  const q = query.trim();
  if (q.length < 2) return Promise.resolve([] as AirportSuggestion[]);
  const qs = new URLSearchParams({ q });
  return request<AirportSuggestion[]>(`/api/flights/autocomplete?${qs.toString()}`);
}

export function resolveAirport(query: string) {
  const q = query.trim();
  if (q.length < 2) return Promise.resolve(null as AirportResolution | null);
  const qs = new URLSearchParams({ q });
  return request<AirportResolution>(`/api/flights/resolve?${qs.toString()}`);
}

const ADMIN_TOKEN_KEY = 'skylux_admin_token';

function adminAuthHeaders(): HeadersInit {
  if (typeof sessionStorage === 'undefined') return {};
  const t = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function sendTransactionalEmail(payload: {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  fromOverride?: string;
  smtpOverride?: AgentEmailBackupSmtpConfig;
}): Promise<{ ok: boolean; messageId?: string; from: string }> {
  const smtpOverride = payload.smtpOverride ?? resolveBackupSmtpForCurrentRoute();
  const fromOverride = payload.fromOverride?.trim() || smtpOverride?.mailFrom;
  const res = await fetch(`${API_BASE}/api/email/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...adminAuthHeaders(),
    },
    body: JSON.stringify({
      ...payload,
      ...(smtpOverride ? { smtpOverride } : {}),
      ...(fromOverride ? { fromOverride } : {}),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(typeof body.message === 'string' ? body.message : 'Email send failed');
  }
  return body as { ok: boolean; messageId?: string; from: string };
}

export function recordQuoteSend(payload: {
  client: Record<string, unknown>;
  quote: Record<string, unknown>;
  ticket: Record<string, unknown>;
  flight: Record<string, unknown>;
  emailKind?: 'quote' | 'oaf' | 'ticket';
}) {
  return request<{ ok: boolean; sentAt: string; quoteId: string }>('/api/records/quote-send', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  });
}

export function fetchServerClients() {
  return request<unknown[]>('/api/clients', { headers: { ...adminAuthHeaders() } });
}

export type AppUserInput = {
  username: string;
  displayName: string;
  password: string;
  nickname?: string;
  profileEmail?: string;
  avatar?: string;
  enabled?: boolean;
  smtp?: {
    host: string;
    port: number;
    secure?: boolean;
    user: string;
    pass: string;
    mailFrom: string;
    replyTo?: string;
  };
};

export function listAgents() {
  return request<Agent[]>('/api/agents', { headers: { ...adminAuthHeaders() } });
}

export function fetchAgentPublic(agentId: string) {
  return request<Agent>(`/api/agents/${encodeURIComponent(agentId)}/public`);
}

export function listAppUsers() {
  return request<unknown[]>('/api/users', { headers: { ...adminAuthHeaders() } });
}

export function createAppUser(payload: AppUserInput) {
  return request<unknown>('/api/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  });
}

export function updateAppUser(id: string, payload: Partial<AppUserInput>) {
  return request<unknown>(`/api/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  });
}

export function deleteAppUser(id: string) {
  return request<void>(`/api/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { ...adminAuthHeaders() },
  });
}
